import { Component, OnInit } from "@angular/core";
import { SharedDataService } from "appRootPath/services/shared-data.service";
import {
  ISSUER_STATE_ROLE_AUCTION,
  INVESTOR_STATE_ROLE_AUCTION,
  INSTRUMENTS_MAP,
  ROLES_MAP,
} from "appRootPath/shared/global-meta-data";
import { CpAuthenticationService } from "appRootPath/services/cp-authentication.service";
import {
  Role,
  UserSelectionData,
} from "appRootPath/models/commercial-papers.model";

@Component({
  selector: "app-user-role-change-panel",
  templateUrl: "./user-role-change-panel.component.html",
  styleUrls: ["./user-role-change-panel.component.scss"],
})

/**
 * This component gets loaded immediately after login and is used to handle the logged-in user's company selection, division selection,
 * and roles selections.
 *
 * If the user selects the admin path, the user is redirected to the admin page where they finish the onboarding flow.
 * If the user selects the Issuer or Investor or IPA path, the user is redirected to the respective dashboard.
 *
 * In any case, if the user has a simple company, single division and single role, the user is directly redirected to the respective dashboard.
 */
export class UserRoleChangePanelComponent implements OnInit {
  isSaved = false;
  showUserPathSelection = false;
  showDivisonSelection = false;
  showInstrumentSelection = false;
  showRoleSelection = false;
  storedUserData;
  divisionsToShow;
  instrumentsToShow;
  rolesToShow;

  instrumentNameMap = INSTRUMENTS_MAP;

  roleNameMap = ROLES_MAP;

  defaultUserSelection: UserSelectionData = {
    iscadmin: false,
    divisionId: null,
    divisionName: null,
    instrumentId: null,
    instrumentType: null,
    roleId: null,
    roleType: null,
    hasMultipleRoles: false,
    state_roles: null,
    is_auction_user: false,
  };

  completeUserSelection: UserSelectionData;

  constructor(
    private debtDataStore: SharedDataService,
    private cpAuthService: CpAuthenticationService
  ) {}

  /**
   * Initialises default user selection. Gets stored userdata, parses stored user data.
   */
  ngOnInit(): void {
    this.setDefaultUserSelection();
    this.storedUserData = this.debtDataStore.getUserDataFromLS();
    this.parseUserData();
  }

  /**
   * Sets the Complete User Selection Data to the default User Selection Data.
   */
  setDefaultUserSelection(): void {
    this.completeUserSelection = this.defaultUserSelection;
  }

  /**
   * Parses userData to show paths for the user to select.
   */
  parseUserData() {
    if (this.storedUserData.corporate_profile) {
      this.showUserPathSelection = true;
      this.completeUserSelection.hasMultipleRoles = true;
    } else if (!this.storedUserData.corporate_profile) {
      this.parseUserDivisions();
    }
  }

  /**
   * Parses users division data to show relevant divisions for selection.
   */
  parseUserDivisions() {
    this.divisionsToShow = this.storedUserData.party_profile.company.divisions;
    if (this.divisionsToShow.length === 1) {
      this.onDivisionSelect(this.divisionsToShow[0]);
    } else if (this.divisionsToShow.length > 1) {
      this.showDivisonSelection = true;
      this.completeUserSelection.hasMultipleRoles = true;
    }
  }

  /**
   * Parses User Instruments data to show the relevant instruments for selection.
   * @param divisionDetails - Details of the selected Division.
   */
  parseUserInstruments(divisionDetails) {
    this.instrumentsToShow = divisionDetails.instrument;

    if (this.instrumentsToShow.length === 1) {
      this.onInstrumentSelect(this.instrumentsToShow[0]);
    } else if (this.instrumentsToShow.length > 1) {
      this.showInstrumentSelection = true;
      this.completeUserSelection.hasMultipleRoles = true;
    }
  }

  /**
   * Parses User Roles data to show the relevant roles for selection.
   * @param instrumentDetails - Details of the selected Instrument.
   */
  parseUserRoles(instrumentDetails) {
    this.rolesToShow = instrumentDetails.roles;
    if (this.rolesToShow.length === 1) {
      this.onRoleSelect(this.rolesToShow[0]);
    } else if (this.rolesToShow.length > 1) {
      this.showRoleSelection = true;
      this.completeUserSelection.hasMultipleRoles = true;
    }
  }

  /**
   * Handles the main User Path selection.
   * @param pathSelection - User's selection of main path.
   */
  onUserPathSelect(pathSelection: "admin" | "party_user") {
    if (pathSelection === "admin") {
      this.completeUserSelection.iscadmin = true;
      this.saveAndRedirectUserSelection();
    } else if (pathSelection === "party_user") {
      this.completeUserSelection.iscadmin = false;
      this.showUserPathSelection = false;
      this.parseUserDivisions();
    }
  }

  /**
   * Handles selection of division by user.
   * @param selectedDivision - Details of the selected Division.
   */
  onDivisionSelect(selectedDivision) {
    this.showDivisonSelection = false;
    this.completeUserSelection.divisionId = selectedDivision.id;
    this.completeUserSelection.divisionName = selectedDivision.name;
    this.parseUserInstruments(selectedDivision);
  }

  /**
   * Handles selection of instrument by user.
   * @param selectedInstrument - Details of the selected Instrument.
   */
  onInstrumentSelect(selectedInstrument) {
    this.showInstrumentSelection = false;
    this.completeUserSelection.instrumentId = selectedInstrument.id;
    this.completeUserSelection.instrumentType =
      selectedInstrument.instrument_type;
    this.parseUserRoles(selectedInstrument);
  }

  /**
   * Handles role selection of given Role.
   * @param selectedRole - Details of the selected role.
   */
  onRoleSelect(selectedRole: Role) {
    this.showRoleSelection = false;
    this.completeUserSelection.roleId = selectedRole.id;
    this.completeUserSelection.roleType = selectedRole.role_type;
    this.completeUserSelection.state_roles = selectedRole.state_role;
    this.completeUserSelection.is_auction_user = selectedRole.state_role.some(
      (state_role) => {
        return [
          ISSUER_STATE_ROLE_AUCTION,
          INVESTOR_STATE_ROLE_AUCTION,
        ].includes(state_role.state_role_type);
      }
    );
    this.saveAndRedirectUserSelection();
  }

  /**
   * Handles saving of user selection and redirecting to the appropriate user's dashboard.
   */
  saveAndRedirectUserSelection() {
    this.isSaved = true;
    this.debtDataStore.storeUserSelection(this.completeUserSelection);
    this.cpAuthService.redirector(this.completeUserSelection);
    this.debtDataStore.setUserSelectionState(true);
  }
}
